import React, { createContext, useContext, useState, useEffect, PropsWithChildren } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

interface ThemeContextValue {
  theme: ThemeMode;
  effectiveTheme: EffectiveTheme;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = "jitsu-theme-preference";

/**
 * Detects the system's current color scheme preference
 */
function getSystemPreference(): EffectiveTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Loads theme preference from localStorage
 */
function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch (error) {
    console.error("Failed to load theme preference:", error);
  }
  return "light";
}

/**
 * Resolves the effective theme based on user preference and system settings
 */
function resolveEffectiveTheme(theme: ThemeMode): EffectiveTheme {
  if (theme === "system") {
    return getSystemPreference();
  }
  return theme;
}

/**
 * Theme provider component that manages dark mode state
 */
export const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // Initialize from localStorage to prevent flash
  const [theme, setThemeState] = useState<ThemeMode>(getStoredTheme);
  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() => resolveEffectiveTheme(getStoredTheme()));

  // Fetch theme from server on mount to sync across devices
  useEffect(() => {
    fetch("/api/user/theme")
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch theme");
        return res.json();
      })
      .then(data => {
        const serverTheme = data.theme as ThemeMode;
        const storedTheme = getStoredTheme();

        // If server theme differs from localStorage, use server theme (it's the source of truth)
        if (serverTheme !== storedTheme) {
          setThemeState(serverTheme);
          setEffectiveTheme(resolveEffectiveTheme(serverTheme));
          // Update localStorage to match server
          try {
            localStorage.setItem(STORAGE_KEY, serverTheme);
          } catch (error) {
            console.error("Failed to update localStorage with server theme:", error);
          }
        }
      })
      .catch(error => {
        // If API call fails (e.g., user not logged in), keep using localStorage
        console.debug("Could not fetch theme from server, using local preference:", error);
      });
  }, []);

  // Listen for system preference changes when theme is "system"
  useEffect(() => {
    if (theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      setEffectiveTheme(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [theme]);

  // Apply dark class to document element
  useEffect(() => {
    if (effectiveTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [effectiveTheme]);

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    setEffectiveTheme(resolveEffectiveTheme(newTheme));

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch (error) {
      console.error("Failed to save theme preference:", error);
    }

    // Persist to database
    fetch("/api/user/theme", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme: newTheme }),
    }).catch(error => {
      console.error("Failed to save theme to database:", error);
    });
  };

  return <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme }}>{children}</ThemeContext.Provider>;
};

/**
 * Hook to access theme context
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
